import { z } from "zod";

import { DOCUMENT_TYPE_IDS, documentTypeLabel, documentTypePath, type DocumentTypeId } from "./document-types";
import type { AuditChange } from "./organizations";

// Document Filing: what an Intake document is — one type of the document type catalogue (`./document-types.ts`), or no
// type at all — and what it says about money: who it is with, its number, the day it is dated and the day it is due, and
// its total. An agent reads it once from the document's Markdown, as soon as Bedrock has read the document;
// reconciliation matches bank transactions against it, and payments follow the bills and invoices among it. Filings
// kept before the catalogue name one of ten kinds instead of a type, and are read as filings of the catalogue
// (`readFiling`). The API and the frontend share these.

/** The catalogue a filing names its type in: taxonomy L1-v3 (`docs/document-types.md`). */
export const DOCUMENT_TAXONOMY = "L1-v3";

/**
 * What Document Filing comes to about a document:
 *
 * - `filed` — one type's rule holds, and its neighbours' exclusions rule them out;
 * - `needs-review` — a type it could not tell from another (`runnerUp`), or a document it could not read, with the one
 *   question a person answers to settle it; it still names its best type, when it has one;
 * - `out-of-scope` — no type of the catalogue at all (`OUT_OF_SCOPE_REASONS`), kept and logged as every document is.
 */
export const FILING_OUTCOMES = ["filed", "needs-review", "out-of-scope"] as const;
export type FilingOutcome = (typeof FILING_OUTCOMES)[number];

/** Why a document is no type of the catalogue. */
export const OUT_OF_SCOPE_REASONS = [
	"pitch-deck",
	"marketing",
	"product",
	"personal",
	"notes",
	"candidate-cv",
	"unrelated",
] as const;
export type OutOfScopeReason = (typeof OUT_OF_SCOPE_REASONS)[number];

export const OUT_OF_SCOPE_REASON_LABELS: Readonly<Record<OutOfScopeReason, string>> = {
	"pitch-deck": "Pitch deck",
	marketing: "Marketing",
	product: "Product, code or design",
	personal: "Personal document",
	notes: "Notes or strategy",
	"candidate-cv": "CV of a candidate not hired",
	unrelated: "Not the organization's business",
};

/** Whether a document is signed: only an executed one records what it agrees. Unsigned is a draft. */
export const EXECUTION_STATES = ["draft", "executed"] as const;
export type ExecutionState = (typeof EXECUTION_STATES)[number];

/** What a document does to the instrument it is: makes it, changes it, restates, renews or ends it, or gives notice. */
export const INSTRUMENT_ROLES = ["original", "amendment", "restatement", "renewal", "termination", "notice"] as const;
export type InstrumentRole = (typeof INSTRUMENT_ROLES)[number];

/**
 * Who may need to be kept from a document, from least to most: nobody, the people whose personal data it holds, those
 * not to see what somebody is paid, those outside legal privilege. A type can set a floor that a filing never goes below
 * (Document Filing's catalogue holds them); a filing may go above it.
 */
export const SENSITIVITIES = ["normal", "pii", "compensation", "privileged"] as const;
export type Sensitivity = (typeof SENSITIVITIES)[number];

/** Where an invoice stands by its own words: unpaid, partly paid, or paid. */
export const PAYMENT_STATES = ["unpaid", "partly-paid", "paid"] as const;
export type PaymentState = (typeof PAYMENT_STATES)[number];

/** Which way a commercial contract goes, from the organization's side. */
export const CONTRACT_DIRECTIONS = ["we-buy", "we-sell", "partnership"] as const;
export type ContractDirection = (typeof CONTRACT_DIRECTIONS)[number];

/** What a commercial contract is about. */
export const CONTRACT_SUBJECTS = ["software", "services", "goods", "property", "other"] as const;
export type ContractSubject = (typeof CONTRACT_SUBJECTS)[number];

/**
 * What is true of a document whatever its type, when the document states it: whether it is signed, what it does to its
 * instrument, who may need to be kept from it, where an invoice stands, which way a commercial contract goes and what
 * it is about, and whether an agreement says it does not bind. Each is left out when the document does not say.
 */
export const documentAttributesSchema = z.strictObject({
	executionState: z.enum(EXECUTION_STATES).optional(),
	instrumentRole: z.enum(INSTRUMENT_ROLES).optional(),
	sensitivity: z.enum(SENSITIVITIES).optional(),
	paymentState: z.enum(PAYMENT_STATES).optional(),
	direction: z.enum(CONTRACT_DIRECTIONS).optional(),
	subject: z.enum(CONTRACT_SUBJECTS).optional(),
	binding: z.boolean().optional(),
});
export type DocumentAttributes = z.infer<typeof documentAttributesSchema>;

/** Every value an attribute takes but `binding`'s. */
type AttributeValue =
	ExecutionState | InstrumentRole | Sensitivity | PaymentState | ContractDirection | ContractSubject;

/** What a document's Filing card calls each attribute value it shows; the others it leaves out. */
const ATTRIBUTE_VALUE_LABELS: Readonly<Partial<Record<AttributeValue, string>>> = {
	draft: "Draft",
	amendment: "Amendment",
	restatement: "Restatement",
	renewal: "Renewal",
	termination: "Termination",
	notice: "Notice",
	pii: "Personal data",
	compensation: "Compensation",
	privileged: "Privileged",
	unpaid: "Unpaid",
	"partly-paid": "Partly paid",
	paid: "Paid",
	"we-buy": "We buy",
	"we-sell": "We sell",
	partnership: "Partnership",
	software: "Software",
	services: "Services",
	goods: "Goods",
	property: "Property",
	other: "Other",
};

/**
 * What a document's Filing card calls each of its attributes that says something, in the order of the attributes: a
 * signed document, an original, one nobody needs keeping from and an agreement that binds are what a document is unless
 * it says otherwise, and are not shown.
 */
export function attributeLabels(attributes: DocumentAttributes): string[] {
	const { executionState, instrumentRole, sensitivity, paymentState, direction, subject, binding } = attributes;
	const shown = [executionState, instrumentRole, sensitivity, paymentState, direction, subject].flatMap(value => {
		const label = value === undefined ? undefined : ATTRIBUTE_VALUE_LABELS[value];
		return label === undefined ? [] : [label];
	});
	return binding === false ? [...shown, "Not binding"] : shown;
}

/**
 * What a document says it comes to, all in — its gross total, tax included — in the currency it states: a decimal
 * string, never a float, negative only when the document prints it so, as a credit note written as an invoice does.
 */
export const filedAmountSchema = z.strictObject({
	amount: z.string().regex(/^-?\d{1,13}(?:\.\d{1,5})?$/, "Invalid amount"),
	currency: z.string().regex(/^[A-Z]{3}$/, "Invalid currency"),
});
export type FiledAmount = z.infer<typeof filedAmountSchema>;

/** What a filing says about money, of any taxonomy: each left out when the document does not say it. */
const moneyFields = {
	/**
	 * Who the document is with: whoever is on the other side from the organization — the vendor that issued a bill, the
	 * customer an invoice is for, the shop of a receipt — as the document names them.
	 */
	counterparty: z.string().trim().min(1).max(160).optional(),
	/** The document's own number: an invoice number, a receipt number. */
	number: z.string().trim().min(1).max(80).optional(),
	/** The day the document is dated. */
	issuedOn: z.iso.date().optional(),
	/** The day it is due, for an invoice or a bill that says. */
	dueOn: z.iso.date().optional(),
	total: filedAmountSchema.optional(),
};

/** At most this many characters of the question a filing that needs review puts to a person. */
export const FILING_QUESTION_MAX_LENGTH = 200;

/**
 * What Document Filing files a document as, by the catalogue: its outcome; its type — absent only when it is out of
 * scope, or could not be read; for one that needs review, the type it could not rule out and the question that settles
 * it; for one out of scope, why; its attributes; and what it says about money. The shape alone: which fields an
 * outcome takes, and which attributes a type does, are Document Filing's rules, which it holds a filing to before it
 * takes one.
 */
export const documentFilingSchema = z.strictObject({
	taxonomy: z.literal(DOCUMENT_TAXONOMY),
	outcome: z.enum(FILING_OUTCOMES),
	type: z.enum(DOCUMENT_TYPE_IDS).optional(),
	runnerUp: z.enum(DOCUMENT_TYPE_IDS).optional(),
	question: z.string().trim().min(1).max(FILING_QUESTION_MAX_LENGTH).optional(),
	outOfScope: z.enum(OUT_OF_SCOPE_REASONS).optional(),
	attributes: documentAttributesSchema.optional(),
	...moneyFields,
});
export type DocumentFiling = z.infer<typeof documentFilingSchema>;

/**
 * The kinds Document Filing filed a document as before the catalogue, from the organization's side: `customer-*` ones
 * the organization issued, the rest issued to it — the seven finance kinds `packages/mock-documents` generates — and
 * three more. A filing kept then names one of them, and reading it never rewrites it: only a `contract` or an `other`
 * is rewritten, once, when it is classified.
 */
export const LEGACY_FILED_DOCUMENT_KINDS = [
	"customer-invoice",
	"customer-credit-note",
	"remittance-advice",
	"vendor-invoice",
	"vendor-credit-note",
	"expense-receipt",
	"reimbursement-claim",
	"contract",
	"bank-statement",
	"other",
] as const;
export type LegacyFiledDocumentKind = (typeof LEGACY_FILED_DOCUMENT_KINDS)[number];

export const LEGACY_FILED_DOCUMENT_KIND_LABELS: Readonly<Record<LegacyFiledDocumentKind, string>> = {
	"customer-invoice": "Invoice we issued",
	"customer-credit-note": "Credit note we issued",
	"remittance-advice": "Remittance advice",
	"vendor-invoice": "Vendor invoice",
	"vendor-credit-note": "Vendor credit note",
	"expense-receipt": "Receipt",
	"reimbursement-claim": "Reimbursement claim",
	contract: "Contract",
	"bank-statement": "Bank statement",
	other: "Other",
};

/** A filing kept before the catalogue: its kind, and what the document says about money. */
export const legacyFilingSchema = z.strictObject({ kind: z.enum(LEGACY_FILED_DOCUMENT_KINDS), ...moneyFields });
export type LegacyFiling = z.infer<typeof legacyFilingSchema>;

/** The two legacy kinds no type of the catalogue stands for: a document filed as one is still to be classified. */
export const UNCLASSIFIED_LEGACY_KINDS = ["contract", "other"] as const;
export type UnclassifiedLegacyKind = (typeof UNCLASSIFIED_LEGACY_KINDS)[number];

/** The type each legacy kind is, for the eight the catalogue has one for. */
const LEGACY_TYPES: Readonly<Record<Exclude<LegacyFiledDocumentKind, UnclassifiedLegacyKind>, DocumentTypeId>> = {
	"customer-invoice": "fin.ar.customer_invoice",
	"customer-credit-note": "fin.ar.customer_credit_note",
	"remittance-advice": "fin.ar.remittance",
	"vendor-invoice": "fin.ap.vendor_invoice",
	"vendor-credit-note": "fin.ap.vendor_credit_note",
	"expense-receipt": "fin.ap.expense_receipt",
	"reimbursement-claim": "fin.ap.reimbursement_claim",
	"bank-statement": "fin.bank.statement",
};

function isUnclassified(kind: LegacyFiledDocumentKind): kind is UnclassifiedLegacyKind {
	return (UNCLASSIFIED_LEGACY_KINDS as readonly string[]).includes(kind);
}

/** The type of the catalogue a legacy kind is, or undefined for a contract or anything else. */
export function legacyTypeOf(kind: LegacyFiledDocumentKind): DocumentTypeId | undefined {
	return isUnclassified(kind) ? undefined : LEGACY_TYPES[kind];
}

/**
 * A legacy filing as it is read: filed as the type its kind is, or — a contract or anything else, which no type stands
 * for — needing review with no type, and the kind it was filed as (`legacyKind`), until it is classified.
 */
export const upgradedFilingSchema = z.strictObject({
	taxonomy: z.literal("legacy"),
	outcome: z.enum(["filed", "needs-review"]),
	type: z.enum(DOCUMENT_TYPE_IDS).optional(),
	legacyKind: z.enum(UNCLASSIFIED_LEGACY_KINDS).optional(),
	...moneyFields,
});
export type UpgradedFiling = z.infer<typeof upgradedFilingSchema>;

/** A legacy filing read by the catalogue, the stored filing left as it is. */
export function upgradeFiling({ kind, ...money }: LegacyFiling): UpgradedFiling {
	return isUnclassified(kind)
		? { taxonomy: "legacy", outcome: "needs-review", legacyKind: kind, ...money }
		: { taxonomy: "legacy", outcome: "filed", type: LEGACY_TYPES[kind], ...money };
}

/**
 * What Document Filing read from a document, as the API answers it: a filing by the catalogue, or one kept before it,
 * upgraded (`upgradedFilingSchema`). Either names its outcome, its type when it has one, and what it says about money.
 */
export const filingSchema = z.discriminatedUnion("taxonomy", [documentFilingSchema, upgradedFilingSchema]);
export type Filing = z.infer<typeof filingSchema>;

/** A filing as it is kept — by the catalogue, or before it — read as every reader takes it. */
export function readFiling(kept: DocumentFiling | LegacyFiling): Filing {
	return "kind" in kept ? upgradeFiling(kept) : kept;
}

/**
 * What a filing is in a word or two, for a list or a sentence: its type's label, "Out of scope", or "Needs review" for
 * one with no type yet.
 */
export function filedAsLabel(filing: Filing): string {
	if (filing.type !== undefined) {
		return documentTypeLabel(filing.type);
	}
	return filing.outcome === "out-of-scope" ? "Out of scope" : "Needs review";
}

/**
 * What `GET /api/intake/files/{fileId}/filing` answers: what Document Filing read from the document and when, or that it
 * has not read it — which it cannot until Bedrock has.
 */
export const documentFilingAnswerSchema = z.discriminatedUnion("status", [
	z.strictObject({ status: z.literal("unread") }),
	z.strictObject({ status: z.literal("read"), filing: filingSchema, filedAt: z.iso.datetime() }),
]);
export type DocumentFilingAnswer = z.infer<typeof documentFilingAnswerSchema>;

/** Who an entry names for Document Filing, the agent that reads what a document is and what it says about money. */
export const DOCUMENT_FILING = "Document Filing";

/**
 * The audit entry of what Document Filing read a document as, once it is kept: "Document filed" / "invoice.pdf · Vendor
 * invoice · 1850.00 EUR", with each field it read as a value, under the labels a document's Filing card shows them by —
 * where its type sits in the catalogue, whether it needs review and why it is out of scope, but never the question a
 * review asks, which can quote the document: an entry says what became of a document, never what it holds.
 */
export function documentFiledAudit(
	fileName: string,
	filing: Filing,
): { action: string; detail: string; changes: AuditChange[] } {
	const { type, outcome, counterparty, number, issuedOn, dueOn, total } = filing;
	const amount = total === undefined ? undefined : `${total.amount} ${total.currency}`;
	const outOfScope = filing.taxonomy === DOCUMENT_TAXONOMY ? filing.outOfScope : undefined;
	const fields: readonly (readonly [string, string | undefined])[] = [
		["Filed as", type === undefined ? undefined : documentTypePath(type)],
		["Needs review", outcome === "needs-review" ? "Yes" : undefined],
		["Out of scope", outOfScope === undefined ? undefined : OUT_OF_SCOPE_REASON_LABELS[outOfScope]],
		["With", counterparty],
		["Number", number],
		["Dated", issuedOn],
		["Due", dueOn],
		["Total", amount],
	];
	return {
		action: "Document filed",
		detail: [fileName, filedAsLabel(filing), ...(amount === undefined ? [] : [amount])].join(" · "),
		changes: fields.flatMap(([label, value]) => (value === undefined ? [] : [{ label, value }])),
	};
}

/**
 * The audit entry of a filing kept before the catalogue as a contract or as anything else, once Document Filing has
 * classified it: "Document classified" / "msa.pdf · Master agreement", with where its type sits, whether it needs review
 * and why it is out of scope as its values — never what it says about money, which the filing kept as it was, nor a
 * review's question.
 */
export function documentClassifiedAudit(
	fileName: string,
	filing: Filing,
): { action: string; detail: string; changes: AuditChange[] } {
	const classifying = new Set(["Filed as", "Needs review", "Out of scope"]);
	return {
		action: "Document classified",
		detail: `${fileName} · ${filedAsLabel(filing)}`,
		changes: documentFiledAudit(fileName, filing).changes.filter(change => classifying.has(change.label)),
	};
}
